import { FeedbackRowType, IFeedbackFormStructure } from './types';
import {
  ToggleRowBuilder,
  SelectRowBuilder,
  StarRowBuilder,
  TextAreaRowBuilder,
} from './builders';

export type IBuilder<TType extends FeedbackRowType> = {
  [FeedbackRowType.TextArea]: TextAreaRowBuilder;
  [FeedbackRowType.Select]: SelectRowBuilder;
  [FeedbackRowType.Toggle]: ToggleRowBuilder;
  [FeedbackRowType.Star]: StarRowBuilder;
}[TType];

export class FeedbackFormBuilder {
  #rows: IFeedbackFormStructure['rows'] = [];

  // TODO: remove default value
  #title: string | undefined = 'Form';

  constructor(private id: string) {}

  withTitle(title: string) {
    this.#title = title;
    return this;
  }

  #getBuilderConstructor(type: FeedbackRowType) {
    switch (type) {
      case FeedbackRowType.Select:
        return SelectRowBuilder;

      case FeedbackRowType.Star:
        return StarRowBuilder;

      case FeedbackRowType.Toggle:
        return ToggleRowBuilder;

      case FeedbackRowType.TextArea:
        return TextAreaRowBuilder;
    }
  }
  withRow<TType extends FeedbackRowType>(
    type: TType,
    id: string,
    cb: (builder: IBuilder<TType>) => void,
  ) {
    const BuilderConstructor = this.#getBuilderConstructor(type);

    const builder = new BuilderConstructor(id);
    // @ts-expect-error TODO: fix type issue
    cb(builder);

    const validation = builder.isValid();
    if (!validation.valid) {
      const base = [
        `Invalid builder. Id: ${validation.id}, Type: ${validation.type}`,
      ];

      const messages = validation.messages
        .map((message) => {
          return `- ${message}`;
        })
        .join('\n');

      throw new Error([base, messages].join('\n'));
    }

    this.#rows.push(builder.build());

    return this;
  }

  withTextArea(id: string, cb: (builder: TextAreaRowBuilder) => void) {
    return this.withRow(FeedbackRowType.TextArea, id, cb);
  }
  withDropdown(id: string, cb: (builder: SelectRowBuilder) => void) {
    return this.withRow(FeedbackRowType.Select, id, cb);
  }
  withSegmentedToggle(id: string, cb: (builder: ToggleRowBuilder) => void) {
    return this.withRow(FeedbackRowType.Toggle, id, cb);
  }

  withFacesRatingBar(id: string, cb: (builder: StarRowBuilder) => void) {
    return this.withRow(FeedbackRowType.Star, id, cb);
  }

  isValid() {
    if (!this.id || !this.#title) {
      return false;
    }

    if (!this.#rows.length) {
      return false;
    }

    return true;
  }

  build(): IFeedbackFormStructure {
    return {
      id: this.id,
      title: this.#title!,
      rows: this.#rows,
    };
  }
}
